export type StepDataRow = {
  key: string;
  value: string;
};

export const BACKGROUND_DATA_ROW_ERROR = "background is set with the step's Background button, not as a Data row.";

function normalizeStepDataKey(key: unknown): unknown {
  return typeof key === 'string' ? key.trim().toLowerCase() : key;
}

export function isBackgroundDataKey(key: unknown): boolean {
  return normalizeStepDataKey(key) === 'background';
}

type StepTypeLike = {
  id: number | string;
  fields?: unknown;
};

type FormStepLike = {
  id?: number | string;
  stepTypeId?: number | string;
  text?: string;
  [key: string]: unknown;
};

type StateStepLike = {
  id?: number | string;
  stepTypeId?: number | string;
  text?: string;
  [key: string]: unknown;
};

type LocationLike = {
  id?: number;
  alias?: string;
  title?: string;
  videoId?: number | null;
};

/**
 * `step.data` reaches the form as an array of `{ key, value }` rows, but legacy
 * and freshly-built steps can carry `{}`, `null` or `undefined`. Normalise to an
 * array so callers never have to branch.
 */
export function normalizeStepData(data: unknown): StepDataRow[] {
  if (!Array.isArray(data)) {
    return [];
  }
  return data
    .filter((row): row is StepDataRow => !!row && typeof row === 'object')
    .map((row) => ({
      ...row,
      key: row.key ?? '',
      value: row.value ?? '',
    }));
}

export function hasNewBackgroundDataRow(steps: FormStepLike[] | undefined | null, originalSteps: StateStepLike[] | undefined | null): boolean {
  if (!Array.isArray(steps)) return false;
  const originalsById = new Map((originalSteps ?? []).filter((step) => step.id != null).map((step) => [String(step.id), step]));
  return steps.some((step, index) => {
    const original = step.id != null ? originalsById.get(String(step.id)) : originalSteps?.[index];
    const existingRows = normalizeStepData(original?.data).filter((row) => isBackgroundDataKey(row.key));
    return normalizeStepData(step.data).some((row) => {
      if (!isBackgroundDataKey(row.key)) return false;
      const unchangedLegacyIndex = existingRows.findIndex((existing) => existing.value === row.value);
      if (unchangedLegacyIndex < 0) return true;
      existingRows.splice(unchangedLegacyIndex, 1);
      return false;
    });
  });
}

export function readStepBackground(step: unknown, locations?: LocationLike[] | null): string {
  if (!step || typeof step !== 'object' || Array.isArray(step)) {
    const rows = normalizeStepData(step).filter((item) => isBackgroundDataKey(item.key));
    return rows.length ? rows[rows.length - 1].value : '';
  }
  const value = step as { backgroundLocationId?: number | null; data?: unknown };
  if (Object.prototype.hasOwnProperty.call(value, 'backgroundLocationId')) {
    return value.backgroundLocationId == null ? '' : String(value.backgroundLocationId);
  }
  const rows = normalizeStepData(value.data).filter((item) => isBackgroundDataKey(item.key));
  const alias = rows.length ? rows[rows.length - 1].value : '';
  const matches = (locations ?? []).filter((location) => location.alias === alias);
  return matches.length === 1 && matches[0].id != null ? String(matches[0].id) : alias;
}

export function writeStepBackground<T extends object>(step: T, locationId: string): T & { backgroundLocationId?: number | null } {
  const value = step as { data?: unknown };
  if (locationId === '' && !Object.prototype.hasOwnProperty.call(step, 'backgroundLocationId') && !normalizeStepData(value.data).some((row) => isBackgroundDataKey(row.key))) return step as T & { backgroundLocationId?: number | null };
  const id = locationId === '' ? null : Number(locationId);
  if (id != null && (!Number.isSafeInteger(id) || id <= 0)) {
    throw new Error('Step background requires a location id');
  }
  return { ...step, backgroundLocationId: id };
}

export function mergeStepBackgrounds<T extends FormStepLike>(formSteps: T[] | undefined | null, stateSteps: StateStepLike[] | undefined | null): T[] | undefined | null {
  if (!Array.isArray(formSteps) || !Array.isArray(stateSteps)) return formSteps;
  return formSteps.map((step, index) => {
    const state = stateSteps[index];
    if (state?.backgroundSourceStepId != null && !Object.prototype.hasOwnProperty.call(state, 'backgroundLocationId')) return { ...step, backgroundSourceStepId: state.backgroundSourceStepId, data: state.data };
    const merged = Object.prototype.hasOwnProperty.call(state ?? {}, 'backgroundLocationId')
      ? { ...step, backgroundLocationId: state.backgroundLocationId }
      : step;
    if (!Object.prototype.hasOwnProperty.call(merged, 'backgroundLocationId')) return merged;
    return merged;
  });
}

export type StepBackgroundOption = {
  label: string;
  value: string;
  type: 'none' | 'image' | 'video';
  alias?: string;
};

export type StepBackgroundGroup = {
  label: string;
  options: StepBackgroundOption[];
};

/**
 * The options the per-step background picker offers.
 *
 * Pure, and kept out of the component, because the one rule that matters here is
 * a correctness rule rather than a presentation one: when `allowVideoLocations`
 * is false because the node contains a Video step, the Video group is not built
 * at all. `findVideoBackgroundConflicts` is the save-time check for values that
 * arrive by other routes.
 *
 * PHASE B: this picker rule applies to every step in a node with a Video step.
 * Relax the node-location guard and the node-wide per-step background rule
 * together in the same gated change, never independently.
 */
export function buildStepBackgroundOptions(
  locations: LocationLike[] | undefined | null,
  allowVideoLocations: boolean,
): StepBackgroundGroup[] {
  const selectable = (Array.isArray(locations) ? locations : [])
    .filter((location) => !!location.alias && location.id != null);

  const groups: StepBackgroundGroup[] = [
    {
      label: 'Default',
      options: [{ label: 'Node location', value: '', type: 'none' }],
    },
    {
      label: 'Images',
      options: selectable
        .filter((location) => !location.videoId)
        .map((location) => ({
          label: location.title ?? location.alias ?? '',
          value: String(location.id),
          type: 'image' as const,
          alias: location.alias,
        })),
    },
  ];

  if (allowVideoLocations) {
    groups.push({
      label: 'Video',
      options: selectable
        .filter((location) => !!location.videoId)
        .map((location) => ({
          label: location.title ?? location.alias ?? '',
          value: String(location.id),
          type: 'video' as const,
          alias: location.alias,
        })),
    });
  }

  return groups;
}

/**
 * Does the editor render a text input for this step type? Driven by the
 * StepType's `fields` list exactly as StepsForm does, so the answer here and the
 * form's rendering decision cannot drift.
 */
export function stepTypeRendersText(
  stepTypes: StepTypeLike[] | undefined | null,
  stepTypeId: number | string | undefined,
): boolean {
  if (!Array.isArray(stepTypes) || stepTypeId === undefined || stepTypeId === null) {
    return false;
  }
  const stepType = stepTypes.find((type) => Number(type.id) === Number(stepTypeId));
  if (!stepType || !Array.isArray(stepType.fields)) {
    return false;
  }
  return stepType.fields.includes('text');
}

/**
 * Restore `text` on saved steps whose type renders no text input.
 *
 * `serialize()` reads the form DOM and, with `empty` unset, omits inputs whose
 * value is ''. So a missing `text` key is ambiguous: it is either a type that
 * renders no text field at all, or a text-bearing step the author deliberately
 * cleared. Three conditions separate the case worth restoring from the rest:
 *
 * 1. The step must already exist in the database (it has an `id`). A brand new
 *    step has no persisted text, so there is nothing to preserve.
 * 2. Its type must be UNCHANGED since load. Retyping a step into a type that
 *    renders no text is a deliberate act, and `edit_context.js` clears text on
 *    exactly that transition ("clear incompatible fields"); preserving it here
 *    would contradict the established type-change semantics.
 * 3. The CURRENT type must render no text input. A type that does render one
 *    keeps whatever `serialize()` said, so clearing text still works.
 *
 * `originalSteps` is the pristine copy of the steps as loaded (AddBranch's
 * `stepConst`), joined BY ID rather than by index so that deleting or reordering
 * steps before saving cannot copy one step's text onto another.
 */
export function preserveUnrenderedStepText<T extends FormStepLike>(
  formSteps: T[] | undefined | null,
  stateSteps: StateStepLike[] | undefined | null,
  stepTypes: StepTypeLike[] | undefined | null,
  originalSteps?: StateStepLike[] | null,
): T[] | undefined | null {
  if (!Array.isArray(formSteps) || !Array.isArray(stateSteps)) {
    return formSteps;
  }

  const originalsById = new Map<string, StateStepLike>();
  if (Array.isArray(originalSteps)) {
    originalSteps.forEach((step) => {
      if (step && step.id !== undefined && step.id !== null) {
        originalsById.set(String(step.id), step);
      }
    });
  }

  return formSteps.map((formStep, i) => {
    if (formStep?.text !== undefined) {
      return formStep;
    }

    const stateStep = stateSteps[i];

    // formSteps[i] and stateSteps[i] are the same step by construction: the input
    // names carry the state index literally (`steps[${stepIndex}][...]`) and
    // form-serialize assigns to that array position, so this join does not depend
    // on DOM order, drag/drop or render timing.
    const stepId = formStep?.id ?? stateStep?.id;
    if (stepId === undefined || stepId === null) {
      return formStep;
    }

    const original = originalsById.get(String(stepId));
    if (!original || !original.text) {
      return formStep;
    }

    const currentTypeId = formStep?.stepTypeId ?? stateStep?.stepTypeId;
    if (Number(original.stepTypeId) !== Number(currentTypeId)) {
      return formStep;
    }

    if (stepTypeRendersText(stepTypes, currentTypeId)) {
      return formStep;
    }

    return { ...formStep, text: original.text };
  });
}

function findLocationByAlias(
  locations: LocationLike[] | undefined | null,
  alias: string,
): LocationLike | undefined {
  if (!Array.isArray(locations)) {
    return undefined;
  }
  const matches = locations.filter((location) => location.alias === alias);
  return matches.length === 1 ? matches[0] : undefined;
}

function findStepBackgroundLocation(step: FormStepLike, locations: LocationLike[] | undefined | null): LocationLike | undefined {
  const value = readStepBackground(step);
  return Object.prototype.hasOwnProperty.call(step, 'backgroundLocationId')
    ? (locations ?? []).find((location) => String(location.id) === value)
    : findLocationByAlias(locations, value);
}

export type StepBackgroundIssue = {
  /** Zero-based position, for programmatic use. */
  stepIndex: number;
  alias: string;
  locationTitle?: string;
  stepTypeId?: number | string;
};

/**
 * How a step is named to an author: ONE-BASED, and with its type, because "Step
 * 0" reads like a bug and a bare number is hard to find in a long node. Matches
 * the wording the publish validator uses for the same step.
 */
export function describeStep(
  stepTypes: StepTypeLike[] | undefined | null,
  stepTypeId: number | string | undefined,
  stepIndex: number,
): string {
  const stepNumber = stepIndex + 1;
  const stepType = Array.isArray(stepTypes)
    ? stepTypes.find((type) => Number(type.id) === Number(stepTypeId))
    : undefined;
  const title = (stepType as { title?: string; value?: string } | undefined);
  const name = title?.title ?? title?.value;
  return name ? `Step ${stepNumber} (${name})` : `Step ${stepNumber}`;
}

/**
 * Steps whose per-step background resolves to a VIDEO-backed location when the
 * node contains a Video step.
 *
 * This is the node-wide rule that complements the node-level guard in
 * `AddBranch.formValidated` ("Cannot use a Video Step Type with a Video Location
 * selected"), which only inspects the node's `locationId` and cannot see a
 * per-step override.
 * The measured runtime failure is that the scene's ambient video player is
 * destroyed mid-playback when the step's video loads, and nothing ever restores
 * it, leaving the reader on the frozen last frame of the step clip for the rest
 * of the node.
 *
 * PHASE B: this check covers every step when the node contains a Video step.
 * Relax the node-location guard and the node-wide per-step background rule
 * together in the same gated change, never independently.
 */
export function findVideoBackgroundConflicts(
  steps: FormStepLike[] | undefined | null,
  locations: LocationLike[] | undefined | null,
  videoStepTypeId: number,
): StepBackgroundIssue[] {
  if (!Array.isArray(steps)) {
    return [];
  }
  const hasVideoStep = steps.some((step) => step && Number(step.stepTypeId) === Number(videoStepTypeId));
  if (!hasVideoStep) {
    return [];
  }

  const issues: StepBackgroundIssue[] = [];
  steps.forEach((step, stepIndex) => {
    if (!step) {
      return;
    }
    const alias = readStepBackground(step, locations);
    if (!alias) {
      return;
    }
    const location = findStepBackgroundLocation(step, locations);
    if (location && location.videoId) {
      issues.push({ stepIndex, alias: location.alias ?? alias, locationTitle: location.title, stepTypeId: step.stepTypeId });
    }
  });
  return issues;
}

/**
 * Steps whose per-step background names an alias that no location on the book
 * carries — normally because the location was deleted or renamed after the step
 * was authored.
 *
 * This is worth surfacing loudly: the runtime client resolves the alias directly
 * against the exported `scenes` map and reads `scenes[alias].audio` WITHOUT a
 * presence guard, so an unresolvable alias throws inside the step load rather
 * than falling back to the node's location.
 */
export function findUnresolvedBackgrounds(
  steps: FormStepLike[] | undefined | null,
  locations: LocationLike[] | undefined | null,
): StepBackgroundIssue[] {
  if (!Array.isArray(steps)) {
    return [];
  }

  const issues: StepBackgroundIssue[] = [];
  steps.forEach((step, stepIndex) => {
    if (!step) {
      return;
    }
    const legacy = normalizeStepData(step.data).filter((row) => isBackgroundDataKey(row.key));
    const referenced = Object.prototype.hasOwnProperty.call(step, 'backgroundLocationId');
    if (referenced && step.backgroundLocationId == null) return;
    if (!referenced && !legacy.length) return;
    const alias = referenced ? readStepBackground(step, locations) : legacy[legacy.length - 1].value;
    const matches = (locations ?? []).filter((location) => referenced ? String(location.id) === alias : location.alias === legacy[legacy.length - 1].value);
    if (matches.length !== 1 || (!referenced && new Set(legacy.map((row) => row.value)).size > 1)) {
      issues.push({ stepIndex, alias, stepTypeId: step.stepTypeId });
    }
  });
  return issues;
}
